import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import MainHeader from './MainHeader';
import { PageBanner, PagePopup, PageSections } from '../PageExtras';
import { NewsletterSignup } from '../NewsletterSignup';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function MainLayout() {
  const location = useLocation();
  
  // Derive page name from route for Page Extras
  const currentPage = (() => {
    const path = location.pathname.replace(/^\//, '').split('/')[0];
    return path || 'homepage';
  })();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Skip Link for Accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header */}
      <MainHeader />
      <PageBanner page={currentPage} />
      <PageSections page={currentPage} position="top" />

      {/* Main Content */}
      <main id="main-content" className="flex-1" role="main" aria-label="Page content">
        <Outlet />
      </main>
      <PageSections page={currentPage} position="bottom" />
      <PagePopup page={currentPage} />

      {/* Footer */}
      <footer className="bg-rsc-navy text-white py-16" role="contentinfo" aria-label="Site footer">
        <div className="container-app">
          {/* APS Training Academy Banner */}
          <div className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-rsc-yellow/10 to-transparent border border-rsc-yellow/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <a href="https://www.apstrainingacademy.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 flex-shrink-0">
                <img 
                  src="https://www.apstrainingacademy.org/wp-content/uploads/2024/05/APSSTA-000_Icon__Yellow-iso.png" 
                  alt="APS Training Academy" 
                  className="h-16 w-auto"
                />
                <div>
                  <p className="font-heading font-bold text-white text-lg">APS Training Academy</p>
                  <p className="text-rsc-yellow text-sm font-semibold">501(c)(3) Non-Profit</p>
                </div>
              </a>
              <div className="flex-1 text-center md:text-left">
                <p className="text-white/80 text-sm">Empowering Communities Through FREE STEAM Education. Ready Set STEM is the educational platform powering APS Training Academy's mission to make STEM education accessible to every child.</p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Link to="/community-impact" className="px-4 py-2 bg-rsc-yellow text-rsc-navy font-bold rounded-full text-sm hover:bg-rsc-yellow-bright transition-colors">Our Impact</Link>
                <a href="https://www.apstrainingacademy.org" target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-white/30 text-white rounded-full text-sm hover:bg-white/10 transition-colors">Visit APS</a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img 
                  src="/brand/rss-logo-horizontal.png" 
                  alt="Ready Set STEM" 
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-sm text-white/70">
                Ready Set STEM is a leading STEM education provider, offering coding, robotics & hands-on learning for K-8 students.
              </p>
            </div>

            {/* Programs */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Programs</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/robotics" className="hover:text-rsc-yellow transition-colors font-semibold text-rsc-yellow/80">Robotics Division</Link></li>
                <li><Link to="/catalog" className="hover:text-rsc-yellow transition-colors">All Courses</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/blog" className="hover:text-rsc-yellow transition-colors">Blog</Link></li>
                <li><Link to="/faq" className="hover:text-rsc-yellow transition-colors">FAQ</Link></li>
                <li><Link to="/our-story" className="hover:text-rsc-yellow transition-colors">Our Story</Link></li>
                <li><Link to="/about" className="hover:text-rsc-yellow transition-colors">About Us</Link></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">Connect</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/contact" className="hover:text-rsc-yellow transition-colors">Contact Us</Link></li>
                <li><Link to="/locations" className="hover:text-rsc-yellow transition-colors">Locations</Link></li>
                <li><Link to="/community-impact" className="hover:text-rsc-yellow transition-colors">Community Impact</Link></li>
                <li><Link to="/leaderboard" className="hover:text-rsc-yellow transition-colors">Leaderboard</Link></li>
                <li><Link to="/showcase" className="hover:text-rsc-yellow transition-colors">Student Showcase</Link></li>
              </ul>
              <div className="mt-4">
                <p className="text-xs text-white/50 mb-2">Get STEM tips in your inbox:</p>
                <NewsletterSignup source="footer" variant="compact" />
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-sm text-white/50">
            <p>© {new Date().getFullYear()} Ready Set STEM. All rights reserved.</p>
            <div className="mt-2 flex justify-center gap-4">
              <Link to="/terms" className="hover:text-white/80 transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white/80 transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
